<template>
  <div class="pse">
    <div class="pse__stage" aria-hidden="true">
      <pin-preview :pin-style="draftStyle" :size="previewSize" :alt="$t('pinStyle.previewAlt')" />
    </div>

    <div class="pse__controls">
      <div class="pse__field">
        <span class="pse__label">{{ $t('pinStyle.colorLabel') }}</span>
        <color-swatches
          v-model="draftColor"
          :presets="PIN_COLORS"
          :label="$t('pinStyle.colorLabel')"
          :custom-label="$t('pinStyle.customColor')"
        />
      </div>

      <div class="pse__field">
        <span class="pse__label">{{ $t('pinStyle.iconLabel') }}</span>
        <div class="d-flex flex-wrap align-items-center" style="gap: 8px">
          <span class="pse__icon" :class="{ 'pse__icon--empty': !draftIcon }">
            <i v-if="draftIcon" :class="draftIcon" aria-hidden="true" />
          </span>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="showPicker = !showPicker">
            {{ draftIcon ? $t('pinStyle.changeIcon') : $t('pinStyle.chooseIcon') }}
          </button>
          <button v-if="draftIcon" type="button" class="btn btn-sm btn-link text-danger px-50" @click="draftIcon = null">
            {{ $t('pinStyle.noIcon') }}
          </button>
        </div>
      </div>

      <div class="pse__field">
        <span class="pse__label">{{ $t('pinStyle.sizeLabel') }}</span>
        <div class="pse__size-row d-flex align-items-center">
          <input
            v-model.number="draftSize"
            type="range"
            class="pse__range"
            :min="SIZE_MIN"
            :max="SIZE_MAX"
            step="2"
            :aria-label="$t('pinStyle.sizeLabel')"
          >
          <span class="pse__size">{{ draftSize }} px</span>
          <button
            type="button"
            class="btn btn-sm btn-link px-50 flex-shrink-0"
            :class="{ invisible: draftSize === SIZE_DEFAULT }"
            :tabindex="draftSize === SIZE_DEFAULT ? -1 : 0"
            @click="draftSize = SIZE_DEFAULT"
          >
            {{ $t('pinStyle.sizeReset') }}
          </button>
        </div>
      </div>

      <div class="pse__field">
        <span class="pse__label">{{ $t('pinStyle.iconColorLabel') }}</span>
        <color-swatches
          v-model="draftIconColor"
          :presets="ICON_COLORS"
          :label="$t('pinStyle.iconColorLabel')"
          :custom-label="$t('pinStyle.customColor')"
        />
      </div>
    </div>

    <div v-if="showPicker" class="pse__picker">
      <fa-icon-picker :value="draftIcon" @select="pickIcon" />
    </div>

    <div class="pse__footer">
      <b-button variant="primary" size="sm" :disabled="saving" @click="apply">
        <b-spinner v-if="saving" small class="mr-25" />
        {{ $t('pinStyle.apply') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import ColorSwatches from './ColorSwatches.vue'
import FaIconPicker from './FaIconPicker.vue'
import PinPreview from './PinPreview.vue'
import {
  DEFAULT_ICON_COLOR, PIN_SIZE_DEFAULT, PIN_SIZE_MAX, PIN_SIZE_MIN,
} from '../utils/pinMarker'

const PIN_COLORS = ['#1f5fd1', '#00a1e0', '#28c76f', '#ff9f43', '#ea5455', '#7367f0', '#e83e8c', '#6e6b7b']
const ICON_COLORS = [DEFAULT_ICON_COLOR, '#16202e', '#ffd43b']
const DEFAULT_COLOR = PIN_COLORS[0]

export default {
  name: 'PinStyleEditor',

  components: { ColorSwatches, FaIconPicker, PinPreview },

  props: {
    // endpoint (PUT) que grava o pin: ex. `dispatch_types/12/pin-style`
    endpoint: { type: String, required: true },
    // pin atual da entidade (null quando ela não tem pin montado por cor)
    color: { type: String, default: null },
    iconClass: { type: String, default: null },
    iconColor: { type: String, default: null },
    size: { type: Number, default: null },
  },

  data() {
    return {
      PIN_COLORS,
      ICON_COLORS,
      SIZE_MIN: PIN_SIZE_MIN,
      SIZE_MAX: PIN_SIZE_MAX,
      SIZE_DEFAULT: PIN_SIZE_DEFAULT,
      draftColor: this.color || DEFAULT_COLOR,
      draftIcon: this.iconClass || null,
      draftIconColor: this.iconColor || DEFAULT_ICON_COLOR,
      draftSize: this.size || PIN_SIZE_DEFAULT,
      showPicker: false,
      saving: false,
    }
  },

  computed: {
    draftStyle() {
      return {
        color: this.draftColor,
        icon_class: this.draftIcon,
        icon_color: this.draftIconColor,
        size: this.draftSize,
      }
    },
    // A prévia mostra o pin no tamanho real que ele terá no mapa.
    previewSize() {
      return this.draftSize
    },
  },

  watch: {
    // A tela troca de tipo/disciplina ou o pin é removido: o rascunho acompanha.
    color(v) { this.draftColor = v || DEFAULT_COLOR },
    iconClass(v) { this.draftIcon = v || null },
    iconColor(v) { this.draftIconColor = v || DEFAULT_ICON_COLOR },
    size(v) { this.draftSize = v || PIN_SIZE_DEFAULT },
  },

  methods: {
    pickIcon(iconClass) {
      this.draftIcon = iconClass
      this.showPicker = false
    },

    async apply() {
      this.saving = true
      try {
        const res = await this.$http.put(`${this.$store.state.api}${this.endpoint}`, {
          pin_color: this.draftColor,
          pin_icon_class: this.draftIcon,
          pin_icon_color: this.draftIconColor,
          pin_size: this.draftSize,
        })
        this.$emit('saved', {
          pin_color: res.data.pin_color,
          pin_icon_class: res.data.pin_icon_class,
          pin_icon_color: res.data.pin_icon_color,
          pin_size: res.data.pin_size,
        })
        this.$message(this.$t('nodeDisciplineConfig.messages.successTitle'), this.$t('pinStyle.applied'), 'success')
      } catch (e) {
        this.$message(this.$t('nodeDisciplineConfig.messages.errorTitle'), this.$t('pinStyle.failed'), 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.pse {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px 20px;
  padding: 16px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
}

.pse__stage {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 136px;
  min-height: 136px;
  background:
    linear-gradient(#eef0f4 1px, transparent 1px) 0 0 / 24px 24px,
    linear-gradient(90deg, #eef0f4 1px, transparent 1px) 0 0 / 24px 24px,
    #f6f7fa;
  border-radius: 10px;
}

.pse__controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.pse__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pse__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6e6b7b;
}

.pse__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  font-size: 18px;
  color: #5e5873;
  background: #f6f7fa;
  border-radius: 8px;
}

.pse__icon--empty {
  border: 1px dashed #d8d6de;
  background: transparent;
}

.pse__size-row {
  gap: 8px;
  min-width: 0;
}

/* min-width baixo: em card estreito o slider encolhe em vez de empurrar o "Padrão" para fora. */
.pse__range {
  flex: 1 1 0;
  min-width: 48px;
  max-width: 260px;
  accent-color: #7367f0;
}

/* Largura fixa: o texto ("24 px" a "128 px") e o botão Padrão não podem mudar a largura do slider durante o arraste. */
.pse__size {
  flex: none;
  width: 48px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #5e5873;
}

.pse__picker {
  grid-column: 1 / -1;
  padding: 12px;
  background: #f6f7fa;
  border-radius: 8px;
}

.pse__footer {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 520px) {
  .pse {
    grid-template-columns: 1fr;
  }

  .pse__stage {
    width: 100%;
  }
}
</style>
